import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import useCashierProducts from '../../hooks/useCashierProducts'
import { API_ENDPOINTS } from '../../config/api'
import offlineApi from '../../utils/offlineApi'

const brandColors = {
  primary: '#1338b5',
  mid: '#1f4dc7',
  bright: '#5c8ef0',
}

// Image Modal Component
const ImageModal = ({ imageUrl, alt, onClose }) => {
  useEffect(() => {
    if (!imageUrl) return

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden' // Prevent background scrolling

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [imageUrl, onClose])

  if (!imageUrl) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        cursor: 'pointer',
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'relative',
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="btn-close btn-close-white"
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '-40px',
            right: 0,
            zIndex: 10000,
            fontSize: '24px',
            opacity: 0.9,
          }}
          aria-label="Close"
        />
        <img
          src={imageUrl}
          alt={alt}
          style={{
            maxWidth: '100%',
            maxHeight: '90vh',
            objectFit: 'contain',
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          }}
        />
      </div>
    </div>
  )
}

// Lazy loading image component
const LazyImage = ({ src, alt, fallback, className, style, onClick }) => {
  const [imageSrc, setImageSrc] = useState(null)
  const [hasError, setHasError] = useState(false)
  const imgRef = useRef(null)
  const imageElementRef = useRef(null)
  const observerRef = useRef(null)

  useEffect(() => {
    // Reset state when src changes
    setImageSrc(null)
    setHasError(false)

    if (!src) {
      setHasError(true)
      return
    }

    // For small thumbnails (<= 100px), load immediately
    // For larger images, use lazy loading with IntersectionObserver
    const width = style?.width
    const isSmallThumbnail = width && (
      (typeof width === 'number' && width <= 100) ||
      (typeof width === 'string' && parseInt(width) <= 100)
    )
    
    if (isSmallThumbnail) {
      // Load small thumbnails immediately
      setImageSrc(src)
      return
    }

    // Check if IntersectionObserver is supported
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback: load immediately if IntersectionObserver is not supported
      setImageSrc(src)
      return
    }

    // Create Intersection Observer for lazy loading larger images
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setImageSrc(src)
            if (observerRef.current) {
              observerRef.current.disconnect()
              observerRef.current = null
            }
          }
        })
      },
      {
        rootMargin: '50px', // Start loading 50px before the image enters viewport
      }
    )

    observerRef.current = observer
    
    // Use a small delay to ensure ref is set
    const checkViewport = () => {
      const currentRef = imgRef.current
      if (currentRef) {
        // Check if element is already in viewport
        const rect = currentRef.getBoundingClientRect()
        const isInViewport = 
          rect.top < window.innerHeight + 50 &&
          rect.bottom > -50 &&
          rect.left < window.innerWidth + 50 &&
          rect.right > -50

        if (isInViewport) {
          // Already visible, load immediately
          setImageSrc(src)
        } else {
          // Observe for when it enters viewport
          observer.observe(currentRef)
        }
      } else {
        // Retry if ref not ready
        setTimeout(checkViewport, 10)
      }
    }
    
    checkViewport()

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }
    }
  }, [src, style])


  // If no src or error, show fallback immediately
  if (!src || hasError) {
    return fallback
  }

  return (
    <div 
      ref={imgRef} 
      className={className} 
      style={{ ...style, position: 'relative', cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick && src && !hasError ? onClick : undefined}
    >
      {imageSrc ? (
        <>
          <img
            ref={imageElementRef}
            src={imageSrc}
            alt={alt}
            onError={() => {
              setHasError(true)
            }}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            loading="lazy"
          />
        </>
      ) : (
        // Show fallback while waiting for image to load (lazy loading)
        fallback
      )}
    </div>
  )
}

const NewSale = ({ onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [cartItems, setCartItems] = useState([])
  const [productDetails, setProductDetails] = useState({}) // Map of product_id -> product details with image
  const [selectedImage, setSelectedImage] = useState(null) // For image modal
  const { products, loading, error } = useCashierProducts()

  // Load cart items from sessionStorage on mount
  useEffect(() => {
    try {
      const savedCart = sessionStorage.getItem('cashierCartItems')
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart)
        setCartItems(parsedCart)
      }
    } catch (error) {
      console.error('Unable to load cart from sessionStorage:', error)
    }
  }, [])

  // Fetch product details (including images) when products are loaded
  useEffect(() => {
    if (!products?.length) return

    const fetchProductDetails = async () => {
      try {
        // Get unique product IDs from branch stock
        const productIds = new Set()
        products.forEach((item) => {
          let productId = null
          if (item.product !== undefined && item.product !== null) {
            productId = typeof item.product === 'object' ? item.product.id : item.product
          } else if (item.product_id !== undefined) {
            productId = item.product_id
          }
          if (productId) productIds.add(productId)
        })

        if (productIds.size === 0) return

        // Fetch all products to get image URLs
        const response = await offlineApi.get(API_ENDPOINTS.PRODUCTS)
        const allProducts = Array.isArray(response.data) ? response.data : []

        // Create a map of product_id -> product details
        const detailsMap = {}
        allProducts.forEach((product) => {
          if (product.id && productIds.has(product.id)) {
            const imageUrl = product.image_url || product.image || null
            detailsMap[product.id] = {
              image_url: imageUrl,
            }
          }
        })

        console.log('Product details fetched:', Object.keys(detailsMap).length, 'products with images')
        setProductDetails(detailsMap)
      } catch (err) {
        console.error('Error fetching product details:', err)
        // Don't set error state, just continue without images
      }
    }

    fetchProductDetails()
  }, [products])

  const normalizedProducts = useMemo(() => {
    if (!products?.length) return []

    return products.map((item) => {
      const stock = Number(item.quantity)
      const price = Number(item.selling_price)
      // Get the actual product_id - the API returns 'product' field which is the product ID
      // Handle both cases: product as ID (number) or as object with id property
      let productId = null
      let productImageUrl = null
      if (item.product !== undefined && item.product !== null) {
        if (typeof item.product === 'object') {
          productId = item.product.id
          // Check if product object has image fields
          productImageUrl = item.product.image_url || item.product.image || null
        } else {
          productId = item.product
        }
      } else if (item.product_id !== undefined) {
        productId = item.product_id
      }

      // Get image_url from various possible locations
      // First check if we have it in productDetails map (from products API)
      // Also check if item.product is a full object with image data
      const imageUrl = 
        productDetails[productId]?.image_url ||
        (typeof item.product === 'object' && item.product ? (item.product.image_url || item.product.image) : null) ||
        item.image_url || 
        item.image || 
        productImageUrl ||
        null

      return {
        id: item.id ?? item.product_id ?? item.product_sku, // Keep for cart operations
        product_id: productId, // Store actual product_id for API calls
        name: item.product_name || 'Unnamed product',
        sku: item.product_sku || 'N/A',
        price: Number.isFinite(price) ? price : 0,
        stock: Number.isFinite(stock) ? stock : 0,
        category:
          item.category_name ||
          item.product_category ||
          item.category ||
          'General',
        image_url: imageUrl,
      }
    })
  }, [products, productDetails])

  // Sync cart items with current product data (update stock, price, remove invalid items)
  useEffect(() => {
    if (!normalizedProducts.length) return

    setCartItems((prevCart) => {
      if (!prevCart.length) return prevCart

      const updatedCart = prevCart
        .map((cartItem) => {
          // Find the current product data
          const currentProduct = normalizedProducts.find(
            (p) => p.id === cartItem.id || p.sku === cartItem.sku
          )

          if (!currentProduct) {
            // Product no longer exists, remove from cart
            return null
          }

          // Update cart item with current product data
          return {
            ...cartItem,
            name: currentProduct.name,
            sku: currentProduct.sku,
            price: currentProduct.price,
            stock: currentProduct.stock,
            category: currentProduct.category,
            product_id: currentProduct.product_id,
            image_url: currentProduct.image_url,
            // Adjust quantity if it exceeds current stock
            quantity: Math.min(cartItem.quantity, currentProduct.stock),
          }
        })
        .filter((item) => item !== null && item.stock > 0) // Remove null items and out-of-stock items

      return updatedCart
    })
  }, [normalizedProducts]) // Only run when products are loaded/updated

  // Save cart items to sessionStorage whenever they change
  useEffect(() => {
    try {
      if (cartItems.length > 0) {
        sessionStorage.setItem('cashierCartItems', JSON.stringify(cartItems))
      } else {
        // Only clear if cart is empty (don't clear if we're just loading)
        sessionStorage.removeItem('cashierCartItems')
      }
    } catch (error) {
      console.error('Unable to save cart to sessionStorage:', error)
    }
  }, [cartItems])

  const filteredProducts = useMemo(() => {
    return normalizedProducts.filter((item) => {
      const query = searchTerm.toLowerCase()
      const matchesSearch =
        !searchTerm ||
        item.name.toLowerCase().includes(query) ||
        item.sku.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)

      return matchesSearch
    })
  }, [searchTerm, normalizedProducts])

  const subtotal = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
  }, [cartItems])

  const totalProducts = filteredProducts.length
  const allProductsCount = normalizedProducts.length
  const lowStockProducts = useMemo(() => {
    return filteredProducts.filter((item) => item.stock <= 6).length
  }, [filteredProducts])
  const hasInventory = allProductsCount > 0
  const showEmptyState = !loading && !error && filteredProducts.length === 0
  const showNoInventory = !loading && !error && !hasInventory

  const currency = (value) =>
    value.toLocaleString(undefined, { style: 'currency', currency: 'USD' })

  const handleAddToCart = useCallback((product) => {
    // Prevent adding items with 0 or less stock
    if (product.stock <= 0) {
      return
    }
    setCartItems((prev) => {
      const found = prev.find((item) => item.id === product.id)
      if (found) {
        return prev.map((item) =>
          item.id === product.id
            ? {
                ...item,
                quantity: Math.min(item.quantity + 1, product.stock),
              }
            : item
        )
      }
      return [...prev, { ...product, quantity: 1 }]
    })
  }, [])

  const handleQuantityChange = (id, value) => {
    const nextValue = Math.max(1, Number(value) || 1)
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              quantity: Math.min(nextValue, item.stock),
            }
          : item
      )
    )
  }

  const handleRemoveFromCart = (id) => {
    setCartItems((prev) => prev.filter((item) => item.id !== id))
  }

  // Barcode scanner detection - automatically add to cart when SKU matches exactly
  const barcodeTimeoutRef = useRef(null)
  const lastSearchTermRef = useRef('')
  
  // Global barcode scanner detection (works even when search field is not focused)
  const globalBarcodeBufferRef = useRef('')
  const globalBarcodeTimeoutRef = useRef(null)

  // Global keyboard listener for barcode scanning (works anywhere on the page)
  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      // Check if user is typing in an input, textarea, or contenteditable element
      const activeElement = document.activeElement
      const isInputFocused = 
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.isContentEditable

      // If any input/textarea is focused, skip global handling (let normal input work)
      // The search field has its own barcode detection handler
      if (isInputFocused) {
        return
      }

      // Handle Enter key - process the barcode
      if (event.key === 'Enter' && globalBarcodeBufferRef.current.trim()) {
        event.preventDefault()
        const barcode = globalBarcodeBufferRef.current.trim()
        globalBarcodeBufferRef.current = ''

        // Find products that match the barcode exactly by SKU
        const exactMatches = normalizedProducts.filter(
          (product) => product.sku.toLowerCase() === barcode.toLowerCase()
        )

        // If exactly one product matches and it's in stock, add it to cart
        if (exactMatches.length === 1) {
          const product = exactMatches[0]
          if (product.stock > 0) {
            handleAddToCart(product)
          }
        }

        return
      }

      // Handle regular character input (for barcode scanning)
      // Only capture if no input is focused
      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        // Clear any existing timeout
        if (globalBarcodeTimeoutRef.current) {
          clearTimeout(globalBarcodeTimeoutRef.current)
        }

        // Add character to buffer
        globalBarcodeBufferRef.current += event.key

        // Reset buffer after 100ms of no input (barcode scanners input very quickly)
        globalBarcodeTimeoutRef.current = setTimeout(() => {
          globalBarcodeBufferRef.current = ''
        }, 100)
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleGlobalKeyDown)

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown)
      if (globalBarcodeTimeoutRef.current) {
        clearTimeout(globalBarcodeTimeoutRef.current)
      }
    }
  }, [normalizedProducts, handleAddToCart])

  // Search field barcode detection (when search field is focused)
  useEffect(() => {
    // Clear any existing timeout
    if (barcodeTimeoutRef.current) {
      clearTimeout(barcodeTimeoutRef.current)
    }

    // If search term is empty or hasn't changed, do nothing
    if (!searchTerm || searchTerm === lastSearchTermRef.current) {
      return
    }

    lastSearchTermRef.current = searchTerm

    // Wait for user to finish typing (barcode scanners input very quickly)
    barcodeTimeoutRef.current = setTimeout(() => {
      // Find products that match the search term exactly by SKU
      const exactMatches = normalizedProducts.filter(
        (product) => product.sku.toLowerCase() === searchTerm.toLowerCase().trim()
      )

      // If exactly one product matches and it's in stock, add it to cart
      if (exactMatches.length === 1) {
        const product = exactMatches[0]
        if (product.stock > 0) {
          handleAddToCart(product)
          // Clear the search term after adding to cart
          setSearchTerm('')
          lastSearchTermRef.current = ''
        }
      }
    }, 300) // 300ms delay - fast enough for barcode scanners, but prevents false triggers during manual typing

    // Cleanup timeout on unmount or when searchTerm changes
    return () => {
      if (barcodeTimeoutRef.current) {
        clearTimeout(barcodeTimeoutRef.current)
      }
    }
  }, [searchTerm, normalizedProducts, handleAddToCart])

  const handleNavigateToCheckout = () => {
    if (cartItems.length === 0) return

    // Generate a short unique receipt reference for Decor Masters
    // Example: DM-483275 (prefix + 6‑digit code)
    const now = new Date()
    const day = String(now.getDate()).padStart(2, '0')
    const sec = String(now.getSeconds()).padStart(2, '0')
    const rand = String(Math.floor(Math.random() * 100)).padStart(2, '0')
    const shortCode = `${day}${sec}${rand}` // 6 digits
    const reference = `DM-${shortCode}`

    const payload = {
      cartItems,
      subtotal,
      reference,
      generatedAt: new Date().toISOString(),
    }

    try {
      sessionStorage.setItem('cashierCheckoutDraft', JSON.stringify(payload))
    } catch (error) {
      console.error('Unable to persist checkout draft:', error)
    }

    if (typeof onNavigate === 'function') {
      onNavigate('checkout')
    }
  }

  return (
    <>
      {/* <div className="pagetitle">
        <h1>New Sale</h1>
       
      </div> */}
      <section className="section">
        <div className="row g-4">
          <div className="col-lg-8">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body p-4">
                <div
                  className="d-flex justify-content-between align-items-center flex-wrap mb-4"
                  style={{
                    paddingBottom: '16px',
                    borderBottom: '2px solid #eef1f4',
                  }}
                >
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <h5 className="card-title mb-0">
                      <i className="bi bi-bag-check me-2" />
                      Decor Masters Products
                    </h5>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span
                        className="badge rounded-pill"
                        style={{
                          background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.bright} 100%)`,
                          color: '#fff',
                          padding: '6px 14px',
                          fontSize: '13px',
                          boxShadow: '0 2px 6px rgba(19, 56, 181, 0.25)',
                        }}
                      >
                        Total: <strong className="ms-1">{totalProducts}</strong>
                      </span>
                      <span
                        className="badge rounded-pill"
                        style={{
                          background: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
                          color: '#343a40',
                          padding: '6px 14px',
                          fontSize: '13px',
                          boxShadow: '0 2px 6px rgba(247, 151, 30, 0.3)',
                        }}
                      >
                        Low stock: <strong className="ms-1">{lowStockProducts}</strong>
                      </span>
                      <span
                        className="badge rounded-pill bg-light text-muted border"
                        style={{ padding: '6px 14px', fontSize: '13px' }}
                      >
                        Cart items: <strong className="ms-1">{cartItems.length}</strong>
                      </span>
                    </div>
                  </div>
                 
                </div>

                <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-2 gap-lg-3 mb-3">
                  <div className="text-muted small">
                    
                    <p className="mb-0 text-secondary">
                      Filter by product name, SKU or category
                    </p>
                  </div>
                  <div className="flex-grow-1 w-100">
                    <div
                      className="position-relative"
                      style={{
                        borderRadius: 999,
                        background: '#f7f9fc',
                        boxShadow: 'inset 0 0 0 1px rgba(19, 56, 181, 0.1)',
                      }}
                    >
                      <i
                        className="bi bi-search text-muted position-absolute"
                        style={{ left: 16, top: '50%', transform: 'translateY(-50%)' }}
                      />
                      <input
                        type="search"
                        className="form-control border-0 bg-transparent"
                        style={{
                          padding: '10px 16px 10px 40px',
                          borderRadius: 999,
                          fontSize: 14,
                        }}
                        placeholder="Search products quickly..."
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                      />
                      <div
                        className="position-absolute d-none d-sm-inline-flex align-items-center text-muted text-uppercase"
                        style={{
                          right: 14,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          fontSize: 11,
                          letterSpacing: '0.08em',
                        }}
                      >
                        CTRL · K
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="text-muted small">
                    Showing{' '}
                    <span className="fw-semibold">{filteredProducts.length}</span> of{' '}
                    <span className="fw-semibold">
                      {loading ? '—' : allProductsCount}
                    </span>{' '}
                    products
                  </span>
                  <span
                    className="badge rounded-pill px-3 py-2"
                    style={{
                      backgroundColor: 'rgba(19, 56, 181, 0.08)',
                      color: brandColors.primary,
                      border: '1px solid rgba(19, 56, 181, 0.15)',
                    }}
                  >
                    Made with ❤️ by Decor Masters
                  </span>
                </div>

                {loading && (
                  <div className="alert alert-light border text-center mb-0">
                    <div className="d-inline-flex align-items-center gap-2">
                      <div className="spinner-border text-primary" role="status" />
                      <span className="fw-semibold text-muted">Syncing branch products…</span>
                    </div>
                  </div>
                )}

                {!loading && error && (
                  <div className="alert alert-danger text-center mb-0" role="alert">
                    {error}
                  </div>
                )}

                {showNoInventory && (
                  <div className="alert alert-light border text-center mb-0">
                    No branch stock is available yet. Please check back later.
                  </div>
                )}

                {!loading && !error && hasInventory && (
                  <div className="list-group list-group-flush">
                  {filteredProducts.map((product, index) => (
                    <div
                      key={product.id}
                      className={`list-group-item px-0 py-3 ${
                        index !== filteredProducts.length - 1 ? 'border-bottom' : ''
                      }`}
                    >
                      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                        <div className="d-flex align-items-start gap-3">
                          <LazyImage
                            src={product.image_url}
                            alt={product.name}
                            className="rounded"
                            style={{
                              width: 60,
                              height: 60,
                              flexShrink: 0,
                              overflow: 'hidden',
                              backgroundColor: 'rgba(19, 56, 181, 0.1)',
                            }}
                            onClick={() => product.image_url && setSelectedImage({ url: product.image_url, alt: product.name })}
                            fallback={
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  backgroundColor: 'rgba(19, 56, 181, 0.1)',
                                  color: brandColors.primary,
                                }}
                              >
                                <span className="fw-semibold">
                                  {product.name.charAt(0)}
                                </span>
                              </div>
                            }
                          />
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <h6 className="mb-0">{product.name}</h6>
                              <span className="badge bg-light text-dark border">
                                {product.category}
                              </span>
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-3 small text-muted">
                              <span>
                                SKU <span className="fw-semibold">{product.sku}</span>
                              </span>
                              <span>
                                Stock{' '}
                                {product.stock <= 6 ? (
                                  <span className="badge bg-danger-subtle text-danger rounded-pill">
                                    {product.stock} units
                                  </span>
                                ) : (
                                  <span
                                    className="badge rounded-pill"
                                    style={{
                                      backgroundColor: 'rgba(19, 56, 181, 0.12)',
                                      color: brandColors.primary,
                                    }}
                                  >
                                    {product.stock} units
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="text-md-end ms-md-auto d-flex flex-column align-items-md-end gap-2">
                          <div>
                            <p className="mb-1 text-muted small">Unit Price</p>
                            <p className="fw-bold fs-5 mb-0">{currency(product.price)}</p>
                          </div>
                          {product.stock > 0 ? (
                            <button
                              className="btn btn-sm rounded-pill px-3 text-white"
                              style={{
                                background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.mid} 100%)`,
                                boxShadow: '0 8px 18px rgba(19, 56, 181, 0.25)',
                                border: 'none',
                              }}
                              onClick={() => handleAddToCart(product)}
                            >
                              <i className="bi bi-plus-circle me-1" />
                              Add to cart
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm rounded-pill px-3"
                              style={{
                                background: '#e9ecef',
                                color: '#6c757d',
                                border: 'none',
                                cursor: 'not-allowed',
                              }}
                              disabled
                            >
                              <i className="bi bi-x-circle me-1" />
                              Out of Stock
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {showEmptyState && (
                    <div className="list-group-item px-0 py-4">
                      <div className="alert alert-light border text-center mb-0">
                        No products match “{searchTerm}”. Try a different keyword.
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div
              className="card border-0 shadow-sm h-100"
              style={{
                background:
                  'linear-gradient(180deg, rgba(19,56,181,0.04) 0%, rgba(255,255,255,1) 45%)',
              }}
            >
              <div className="card-body p-3 p-lg-4 d-flex flex-column">
                <div
                  className="d-flex align-items-center justify-content-between pb-2 mb-3"
                  style={{ borderBottom: '1px dashed rgba(19,56,181,0.2)' }}
                >
                  <div>
                    <h6 className="mb-1 text-uppercase text-muted" style={{ letterSpacing: '0.08em' }}>
                      Cart overview
                    </h6>
                    <p className="text-muted small mb-0">
                      {cartItems.length} item{cartItems.length !== 1 && 's'} in cart
                    </p>
                  </div>
                  <span
                    className="badge rounded-pill px-3 py-2 text-white"
                    style={{
                      background: `linear-gradient(135deg, ${brandColors.primary}, ${brandColors.mid})`,
                      fontSize: 12,
                    }}
                  >
                    Live
                  </span>
                </div>

                <div className="flex-grow-1 overflow-auto" style={{ maxHeight: 300 }}>
                  {cartItems.length === 0 && (
                    <div className="alert alert-light border text-center mb-0 small">
                      Your cart is empty. Browse products to get started.
                    </div>
                  )}

                  {cartItems.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-4 border mb-2 p-2"
                      style={{
                        backgroundColor: '#fff',
                        boxShadow: '0 4px 12px rgba(19,56,181,0.05)',
                      }}
                    >
                      <div className="d-flex flex-column gap-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <p className="fw-semibold mb-0" style={{ fontSize: 14 }}>
                              {item.name}
                            </p>
                            <small className="text-muted">SKU {item.sku}</small>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-link p-0 text-danger text-decoration-none"
                            onClick={() => handleRemoveFromCart(item.id)}
                          >
                            Remove
                          </button>
                        </div>
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            style={{ maxWidth: 110 }}
                            min="1"
                            value={item.quantity}
                            onChange={(event) =>
                              handleQuantityChange(item.id, event.target.value)
                            }
                          />
                          <small className="text-muted">of {item.stock} in stock</small>
                          <div className="ms-auto text-end">
                            <p className="text-muted small mb-0">Line total</p>
                            <p className="fw-bold mb-0" style={{ fontSize: 15 }}>
                              {currency(item.price * item.quantity)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-top pt-3 mt-3">
                  <div className="d-flex justify-content-between mb-2 small text-muted">
                    <span>Subtotal</span>
                    <span className="fw-semibold text-dark">{currency(subtotal)}</span>
                  </div>
                  <button
                    className="btn w-100 mt-2 text-white py-2"
                    type="button"
                    style={{
                      background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.mid} 100%)`,
                      border: 'none',
                      borderRadius: 999,
                      fontWeight: 600,
                      boxShadow: cartItems.length === 0 ? 'none' : '0 10px 20px rgba(19, 56, 181, 0.25)',
                    }}
                    disabled={cartItems.length === 0}
                    onClick={handleNavigateToCheckout}
                  >
                    Checkout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {cartItems.length > 0 && (
        <div
          className="shadow"
          style={{
            position: 'fixed',
            right: '24px',
            bottom: '24px',
            zIndex: 1050,
            backgroundColor: '#fff',
            borderRadius: '999px',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            border: '1px solid rgba(19, 56, 181, 0.15)',
            boxShadow: '0 20px 45px rgba(19, 56, 181, 0.15)',
            maxWidth: '90vw',
          }}
        >
          <div>
            <p className="text-muted small mb-0 text-uppercase fw-semibold">Subtotal</p>
            <p className="fw-bold mb-0 fs-5">{currency(subtotal)}</p>
          </div>
          <button
            className="btn text-white"
            type="button"
            style={{
              background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.mid} 100%)`,
              border: 'none',
              borderRadius: '999px',
              paddingInline: '24px',
              paddingBlock: '10px',
              boxShadow: '0 12px 22px rgba(19, 56, 181, 0.25)',
            }}
            onClick={handleNavigateToCheckout}
            disabled={cartItems.length === 0}
          >
            Checkout
          </button>
        </div>
      )}
      {selectedImage && (
        <ImageModal
          imageUrl={selectedImage.url}
          alt={selectedImage.alt}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </>
  )
}

export default NewSale